import { useCallback, useState } from 'react';

/** localStorage flag: sample/demo data is OPT-IN. Off until the user turns it on. */
const ENABLED_KEY = 'sampleDataEnabled';

/** Synchronous read of the enabled flag — used for first-render (pre-paint) decisions. */
export function isSampleEnabled(): boolean {
  try {
    return localStorage.getItem(ENABLED_KEY) === 'true';
  } catch {
    return false;
  }
}

/**
 * Decides whether the demo "sample data" view is active.
 *
 * Sample data is OPT-IN: off by default so a brand-new user sees the real
 * (empty) dashboard with proper empty states. `enable()` turns it on from the
 * empty-state "explore with sample data" link; the banner's Dismiss calls
 * `disable()`. It's also gated on having zero real transactions, so adding a
 * real transaction ends sample mode automatically. The choice persists across
 * reloads.
 */
export function useSampleMode(realTransactionCount: number) {
  const [enabled, setEnabled] = useState<boolean>(isSampleEnabled);

  const active = realTransactionCount === 0 && enabled;

  const enable = useCallback(() => {
    try { localStorage.setItem(ENABLED_KEY, 'true'); } catch { /* ignore storage failures */ }
    setEnabled(true);
  }, []);

  const disable = useCallback(() => {
    try { localStorage.setItem(ENABLED_KEY, 'false'); } catch { /* ignore storage failures */ }
    setEnabled(false);
  }, []);

  return { active, enabled, enable, disable };
}
